import { formatCurrencyValue } from '@/lib/currencyFormatting';
import { formatDateLabel } from '@/lib/formatDate';
import { formatShippingDestination } from '@/lib/shipping';

export function readOrderMessage(messages, path, fallback = '') {
  const resolved = String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((current, segment) => current?.[segment], messages);

  return typeof resolved === 'string' ? resolved : fallback;
}

export function fillOrderMessage(template = '', replacements = {}) {
  return Object.entries(replacements).reduce(
    (value, [key, replacement]) =>
      value.replaceAll(`{{${key}}}`, String(replacement ?? '')),
    String(template || ''),
  );
}

export function formatCurrency(value, currency = 'USD') {
  return formatCurrencyValue(value, { currency });
}

export function getPaymentMethodLabel(paymentMethod, messages = {}) {
  switch (paymentMethod) {
    case 'COD':
      return readOrderMessage(messages, 'payment.COD', 'Cash on Delivery');
    case 'PayDunya':
      return readOrderMessage(
        messages,
        'payment.payDunyaMobileMoney',
        'PayDunya Mobile Money',
      );
    case 'PayDunyaCard':
      return readOrderMessage(
        messages,
        'payment.payDunyaCard',
        'Credit Card via PayDunya',
      );
    default:
      return (
        paymentMethod ||
        readOrderMessage(messages, 'payment.unknown', 'Unknown payment')
      );
  }
}

export function getOrderDisplayStatus(order) {
  if (
    order?.refundStatus === 'processed' ||
    order?.orderStatus === 'refunded'
  ) {
    return 'refunded';
  }

  if (order?.refundStatus === 'requested') {
    return 'refund_requested';
  }

  if (order?.refundStatus === 'failed') {
    return 'refund_failed';
  }

  return order?.orderStatus || 'pending';
}

export function getOrderDisplayStatusLabel(order, messages = {}) {
  switch (getOrderDisplayStatus(order)) {
    case 'refund_requested':
      return readOrderMessage(
        messages,
        'status.refundRequested',
        'Refund Requested',
      );
    case 'refund_failed':
      return readOrderMessage(messages, 'status.refundFailed', 'Refund Failed');
    case 'refunded':
      return readOrderMessage(messages, 'status.refunded', 'Refunded');
    case 'cancelled':
      return readOrderMessage(messages, 'status.cancelled', 'Cancelled');
    case 'completed':
      return readOrderMessage(messages, 'status.completed', 'Completed');
    case 'shipped':
      return readOrderMessage(messages, 'status.shipped', 'Shipped');
    case 'paid':
      return readOrderMessage(messages, 'status.paid', 'Paid');
    default:
      return readOrderMessage(messages, 'status.pending', 'Pending');
  }
}

export function getOrderStatusTone(order) {
  switch (getOrderDisplayStatus(order)) {
    case 'completed':
    case 'shipped':
      return 'hub-pill--accent';
    case 'paid':
      return 'hub-pill--info';
    case 'refund_failed':
      return 'hub-pill--danger';
    case 'refunded':
    case 'cancelled':
      return 'hub-pill--default';
    case 'refund_requested':
    case 'pending':
    default:
      return 'hub-pill--warning';
  }
}

export function canCancelOrRefundOrder(order) {
  if (!order) {
    return false;
  }

  if (order.orderStatus === 'pending') {
    return true;
  }

  return order.orderStatus === 'paid' && order.refundStatus !== 'processed';
}

export function getCancelOrRefundActionLabel(order, messages = {}) {
  if (order?.orderStatus === 'pending') {
    return readOrderMessage(messages, 'admin.cancelOrder', 'Cancel Order');
  }

  if (order?.orderStatus === 'paid') {
    return order?.refundStatus === 'failed'
      ? readOrderMessage(messages, 'admin.retryRefund', 'Retry Refund')
      : readOrderMessage(messages, 'admin.requestRefund', 'Request Refund');
  }

  return '';
}

export function getPaymentStatusMessage(order, messages = {}) {
  if (
    order?.refundStatus === 'processed' ||
    order?.orderStatus === 'refunded'
  ) {
    return {
      text: fillOrderMessage(
        readOrderMessage(
          messages,
          'statusMessages.payment.refundedOn',
          'Refunded on {{date}}.',
        ),
        {
          date:
            formatDateLabel(order.refundedAt) ||
            readOrderMessage(messages, 'detail.recently', 'recently'),
        },
      ),
      tone: 'warning',
    };
  }

  if (order?.refundStatus === 'requested') {
    return {
      text: readOrderMessage(
        messages,
        'statusMessages.payment.refundProcessing',
        'Refund is being processed.',
      ),
      tone: 'warning',
    };
  }

  if (order?.refundStatus === 'failed') {
    return {
      text: readOrderMessage(
        messages,
        'statusMessages.payment.refundFailed',
        'Refund could not be completed yet.',
      ),
      tone: 'error',
    };
  }

  if (order?.orderStatus === 'cancelled') {
    return {
      text: readOrderMessage(
        messages,
        'statusMessages.payment.cancelled',
        'Order was cancelled before payment capture.',
      ),
      tone: 'warning',
    };
  }

  if (order?.paidAt) {
    return {
      text: fillOrderMessage(
        readOrderMessage(
          messages,
          'statusMessages.payment.paidOn',
          'Paid on {{date}}.',
        ),
        { date: formatDateLabel(order.paidAt) },
      ),
      tone: 'success',
    };
  }

  if (['paid', 'shipped', 'completed'].includes(order?.orderStatus)) {
    return {
      text: readOrderMessage(
        messages,
        'statusMessages.payment.confirmed',
        'Payment confirmed.',
      ),
      tone: 'success',
    };
  }

  if (order?.paymentMethod === 'COD') {
    return {
      text: readOrderMessage(
        messages,
        'statusMessages.payment.codDue',
        'Cash on delivery is still due from the customer.',
      ),
      tone: 'warning',
    };
  }

  return {
    text: readOrderMessage(
      messages,
      'statusMessages.payment.pending',
      'Payment has not been captured yet.',
    ),
    tone: 'error',
  };
}

export function getPaidSummaryLabel(order, messages = {}) {
  if (order?.paidAt) {
    return formatDateLabel(order.paidAt);
  }

  if (['paid', 'shipped', 'completed'].includes(order?.orderStatus)) {
    return readOrderMessage(messages, 'detail.confirmed', 'Confirmed');
  }

  return readOrderMessage(messages, 'detail.notYet', 'Not yet');
}

export function getDeliveryStatusMessage(order, messages = {}) {
  if (
    order?.refundStatus === 'processed' ||
    order?.orderStatus === 'refunded'
  ) {
    return {
      text: readOrderMessage(
        messages,
        'statusMessages.delivery.refunded',
        'Delivery stopped because the order was refunded.',
      ),
      tone: 'warning',
    };
  }

  if (order?.orderStatus === 'cancelled') {
    return {
      text: readOrderMessage(
        messages,
        'statusMessages.delivery.cancelled',
        'Delivery stopped because the order was cancelled.',
      ),
      tone: 'warning',
    };
  }

  if (order?.deliveredAt) {
    return {
      text: fillOrderMessage(
        readOrderMessage(
          messages,
          'statusMessages.delivery.deliveredOn',
          'Delivered on {{date}}.',
        ),
        { date: formatDateLabel(order.deliveredAt) },
      ),
      tone: 'success',
    };
  }

  if (['shipped', 'completed'].includes(order?.orderStatus)) {
    return {
      text: readOrderMessage(
        messages,
        'statusMessages.delivery.inProgress',
        'Delivery is in progress.',
      ),
      tone: 'info',
    };
  }

  return {
    text: readOrderMessage(
      messages,
      'statusMessages.delivery.pending',
      'Delivery has not been confirmed yet.',
    ),
    tone: 'error',
  };
}

export function formatShippingAddress(shippingAddress = {}, geolocation = {}) {
  return formatShippingDestination(shippingAddress, geolocation);
}
